// API 문서의 파라미터 표를 렌더하는 컴포넌트
import { Fragment, useEffect, useMemo, useState } from 'react';
import classes from './DescriptionPageTemplate.module.css';

// 검색·필터 툴바를 노출하는 최소 행 수. 파라미터가 2~3개인 Header 표에는 붙이지 않는다
const TOOLBAR_MIN_ROWS = 6;

// 필수로 취급하는 required 값. 문서 데이터에 'O' 와 'Y' 두 표기가 섞여 있다
const REQUIRED_VALUES = ['O', 'Y'];
// 선택으로 취급하는 값. 그 외('X' 등)는 의미가 확인되지 않아 원문을 그대로 보여준다
const OPTIONAL_VALUES = ['', 'N'];

/** 타입 문자열이 객체·배열 계열인지 판정한다. 'String[]' · 'Number(8)' 같은 표기가 섞여 있다 */
function isComplexType(type) {
  return /^(object|array)/i.test(type || '') || /\[\]$/.test(type || '');
}

/** desc 가 문자열일 때만 검색 대상에 넣는다. JSX 는 렌더하지 않고는 텍스트를 얻을 수 없다 */
function searchableText(row) {
  return [row.title, typeof row.desc === 'string' ? row.desc : '']
    .join(' ')
    .toLowerCase();
}

/**
 * 다음 행의 들여쓰기가 더 깊으면 하위 필드를 가진 그룹 행으로 본다.
 * 판정은 항상 원본 배열로 한다. 필터로 자식이 숨겨져도 그룹 표시가 유지되어야 한다.
 */
function buildGroupFlags(data) {
  return data.map((row, i) => {
    const next = data[i + 1];
    return Boolean(next && (next.indent || 0) > (row.indent || 0));
  });
}

/** 문자열 desc 는 줄바꿈을 <br /> 로 바꿔 렌더한다. 기존 TableComponent 의 동작을 유지한다 */
function renderDesc(desc) {
  if (typeof desc !== 'string') return desc;
  return desc.split('\n').map((line, i) => (
    <Fragment key={i}>
      {i ? <br /> : null}
      {line}
    </Fragment>
  ));
}

const ParamsTable = ({ data }) => {
  const [keyword, setKeyword] = useState('');
  const [requiredOnly, setRequiredOnly] = useState(false);

  // 버전 셀렉트로 문서를 바꾸면 필터가 남아 있으면 안 된다
  useEffect(() => {
    setKeyword('');
    setRequiredOnly(false);
  }, [data]);

  const groupFlags = useMemo(() => buildGroupFlags(data), [data]);

  const showToolbar = data.length >= TOOLBAR_MIN_ROWS;
  const normalizedKeyword = keyword.trim().toLowerCase();

  const visibleFlags = data.map((row) => {
    const matchKeyword =
      !normalizedKeyword || searchableText(row).includes(normalizedKeyword);
    const matchRequired = !requiredOnly || REQUIRED_VALUES.includes(row.required);
    return matchKeyword && matchRequired;
  });
  const visibleCount = visibleFlags.filter(Boolean).length;

  const requiredCount = data.filter((row) => REQUIRED_VALUES.includes(row.required)).length;

  return (
    <>
      {showToolbar && (
        <div className={classes.paramsToolbar}>
          <span className={classes.paramsCount}>
            {data.length}개 · 필수 {requiredCount}
          </span>
          <label className={classes.paramsSearch}>
            <svg width="13" height="13" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.6">
              <circle cx="7" cy="7" r="4.5" />
              <path d="M10.5 10.5 14 14" />
            </svg>
            <input
              type="search"
              value={keyword}
              onChange={(e) => setKeyword(e.target.value)}
              placeholder="파라미터 검색"
              aria-label="파라미터 검색"
            />
          </label>
          <button
            type="button"
            className={classes.paramsToggle}
            aria-pressed={requiredOnly}
            onClick={() => setRequiredOnly((prev) => !prev)}
          >
            필수만
          </button>
        </div>
      )}

      <table className={classes.params}>
        {/* 파라미터 열의 `1%` 는 "가능한 좁게" 힌트다. auto 레이아웃에서 최소 콘텐츠 폭까지
            줄어들고, 이름이 nowrap 이라 그 값이 곧 이 문서의 최장 이름 폭이 된다.
            타입·필수는 선호 폭을 둔다. 타입 배지는 줄바꿈이 허용되어 최소 폭이 작아,
            선호 폭이 없으면 불필요하게 두 줄이 된다 */}
        <colgroup>
          <col width="1%" />
          <col width={110} />
          <col width={80} />
          <col />
        </colgroup>
        <thead>
          <tr>
            <th className={classes.headCell}>파라미터</th>
            <th className={classes.headCell}>타입</th>
            <th className={classes.headCell}>필수</th>
            <th className={classes.headCell}>설명</th>
          </tr>
        </thead>
        <tbody>
          {data.map((item, i) => {
            if (!visibleFlags[i]) return null;

            const indent = item.indent || 0;
            const isGroup = groupFlags[i];
            const isRequired = REQUIRED_VALUES.includes(item.required);
            const isOptional = OPTIONAL_VALUES.includes(item.required);

            return (
              <tr key={i} className={isGroup ? classes.groupRow : undefined}>
                <td className={classes.cell}>
                  <span className={classes.paramName}>
                    {/* indent 만큼 트리 가이드를 그린다. 마지막 한 칸이 브랜치다 */}
                    {Array(indent)
                      .fill(null)
                      .map((_, depth) => (
                        <span
                          key={depth}
                          className={
                            depth === indent - 1 ? classes.nameBranch : classes.nameGuide
                          }
                        />
                      ))}
                    <span
                      className={
                        isGroup ? `${classes.nameText} ${classes.nameTextGroup}` : classes.nameText
                      }
                    >
                      {item.title}
                    </span>
                  </span>
                </td>
                <td className={classes.cell}>
                  {item.type && (
                    <span
                      className={
                        isComplexType(item.type)
                          ? `${classes.badgeType} ${classes.badgeTypeComplex}`
                          : classes.badgeType
                      }
                    >
                      {item.type}
                    </span>
                  )}
                </td>
                <td className={classes.cell}>
                  {isRequired && <span className={classes.badgeRequired}>필수</span>}
                  {isOptional && <span className={classes.badgeOptional}>선택</span>}
                  {/* 'O'·'Y'·''·'N' 이 아닌 값은 의미가 확인되지 않았다. 원문을 그대로 보여준다 */}
                  {!isRequired && !isOptional && (
                    <span className={classes.badgeUnknown}>{item.required}</span>
                  )}
                </td>
                <td className={classes.cell}>{renderDesc(item.desc)}</td>
              </tr>
            );
          })}
        </tbody>
      </table>

      {visibleCount === 0 && (
        <div className={classes.paramsEmpty}>검색 결과가 없습니다.</div>
      )}
    </>
  );
};

export default ParamsTable;
